// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Coding, Condition, MedicationRequest, Observation, Patient, Task } from '@medplum/fhirtypes';
import { CLOUDPAT_OBSERVATION_SYSTEM, DME_TASK_CODE_SYSTEM, SUBMIT_DME_ORDER_TASK_CODE } from '@polarissleep/shared';

/** ICD-10 coding systems a Condition.code may carry. */
const ICD10_SYSTEMS = new Set(['http://hl7.org/fhir/sid/icd-10-cm', 'http://hl7.org/fhir/sid/icd-10']);

/** CloudPAT observation code for the apnea-hypopnea index. */
const AHI_OBSERVATION_CODE = 'AH';

/** True when the Task is a submit-dme-order Task generated from an encounter note. */
export function isDmeSubmissionTask(task: Task | undefined): boolean {
  return (
    task?.code?.coding?.some((c) => c.system === DME_TASK_CODE_SYSTEM && c.code === SUBMIT_DME_ORDER_TASK_CODE) ?? false
  );
}

/** Returns the ICD-10 coding from a diagnosis Condition, if present. */
export function getDiagnosisIcd10Coding(condition: Condition | undefined): Coding | undefined {
  return condition?.code?.coding?.find((c) => c.system && ICD10_SYSTEMS.has(c.system));
}

/** True when the Condition carries an ICD-10 coding (i.e. it's a coded diagnosis). */
export function hasIcd10Coding(condition: Condition): boolean {
  return getDiagnosisIcd10Coding(condition) !== undefined;
}

/** True when the Observation is a CloudPAT apnea-hypopnea index measurement. */
export function isAhiObservation(observation: Observation): boolean {
  return (
    observation.code?.coding?.some(
      (c) => c.system === CLOUDPAT_OBSERVATION_SYSTEM && c.code === AHI_OBSERVATION_CODE
    ) ?? false
  );
}

/** Formats an AHI Observation's numeric value, e.g. "32.4 /h". Empty string when absent. */
export function formatAhiValue(observation: Observation | undefined): string {
  const quantity = observation?.valueQuantity;
  if (quantity?.value === undefined) {
    return '';
  }
  const unit = quantity.unit ?? '/h';
  return `${quantity.value} ${unit}`;
}

/**
 * Formats the patient's shipping address as a single line. Prefers a `home`
 * address, falls back to the first non-temporary address. Empty string when none.
 */
export function getPatientShippingAddress(patient: Patient | undefined): string {
  const addresses = patient?.address ?? [];
  if (addresses.length === 0) {
    return '';
  }
  const preferred = addresses.find((a) => a.use === 'home') ?? addresses.find((a) => a.use !== 'temp') ?? addresses[0];
  if (preferred.text) {
    return preferred.text;
  }
  const parts = [...(preferred.line ?? []), preferred.city, preferred.state, preferred.postalCode].filter(Boolean);
  return parts.join(', ');
}

export function getDmeVendor(order: MedicationRequest | undefined): string {
  const note =
    order?.note
      ?.map((item) => item.text)
      .filter(Boolean)
      .join('\n') ?? '';
  return note.match(/(?:^|\n)Vendor:\s*([^\n—]+)/i)?.[1]?.trim() ?? 'Parachute';
}

export function getDmeVendorInstructions(vendor: string): string {
  if (vendor === 'Parachute') {
    return 'Log into Parachute, create the order, attach the signed PDF as a chart note, then route it for signature.';
  }
  if (vendor === 'Daybreak') {
    return 'Fax or email the signed prescription PDF to Daybreak. The PDF is the deliverable.';
  }
  return 'Follow the free-text vendor instructions recorded on the order.';
}
